import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { hasBlockingQueryError } from '@client/trpc/error-classification';
import { DEFAULT_ACTION_COLUMN } from '@client/utils';
import { AI_PROVIDERS } from '@shared/constants';
import { formatDateTime } from '@shared/utils/core/formatting';
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Input, Select, Tag, Tooltip } from 'antd';
import { Coins, Download, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ImportModelPricingModal } from './ImportModelPricingModal';
import { type ModelPricingAction, ModelPricingEdit } from './ModelPricingEdit';

export const Route = createFileRoute('/admin-console/ai/model-pricing')({
    staticData: {
        breadcrumb: {
            title: 'Model pricing',
        },
        ui: {
            icon: Coins,
            title: 'Model pricing',
            subtitle: 'Manage AI model pricing for cost tracking.',
            widthVariant: 'fullWidth',
            menuDescription: 'Configure AI model pricing',
        },
    },
    component: ModelPricing,
});

/** Models within this window of their expiry date are flagged "Expiring soon" (30 days). */
const EXPIRY_SOON_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

type ModelPricingRow = RouterOutputs['admin']['aiModelPricing']['listAiModelPricing']['items'][number];
type QueryParams = RouterInputs['admin']['aiModelPricing']['listAiModelPricing'];

const columnHelper = createColumnHelper<ModelPricingRow>();

function ModelPricing() {
    const { trpc } = useTrpc();
    const { formatCurrency } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('ai');
    const [modelPricingAction, setModelPricingAction] = useState<ModelPricingAction>(['none']);
    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: 50,
    });

    // Filter state for UI
    const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [importModalOpen, setImportModalOpen] = useState(false);

    const {
        data: modelPricing,
        isLoading,
        isFetching,
        refetch,
        error,
    } = trpc.admin.aiModelPricing.listAiModelPricing.useQuery({
        ...queryParams,
        provider: providerFilter as QueryParams['provider'],
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        includeExpired: statusFilter === 'inactive',
    });
    const { mutate: deleteModelPricing } = trpc.admin.aiModelPricing.deleteAiModelPricing.useMutation();
    const { mutate: backfillCosts, isPending: isBackfilling } =
        trpc.admin.aiModelPricing.backfillHistoricalCosts.useMutation();

    const columns = [
        columnHelper.accessor('provider', {
            id: 'provider',
            header: 'Provider',
            cell: ({ value }) => {
                const colorMap: Record<string, string> = {
                    OPENAI: 'blue',
                    GOOGLE: 'green',
                    ANTHROPIC: 'purple',
                };
                return (
                    <Tag variant="outlined" color={colorMap[value] || 'default'}>
                        {value}
                    </Tag>
                );
            },
            width: 120,
        }),
        columnHelper.accessor('modelName', {
            id: 'modelName',
            header: 'Model Name',
            cell: ({ value }) => <span className="font-medium">{value}</span>,
        }),
        columnHelper.accessor('inputCostPer1M', {
            id: 'inputCostPer1M',
            header: 'Input Cost / 1M',
            cell: ({ value }) => `US${formatCurrency(value)}`,
            align: 'right',
        }),
        columnHelper.accessor('outputCostPer1M', {
            id: 'outputCostPer1M',
            header: 'Output Cost / 1M',
            cell: ({ value }) => `US${formatCurrency(value)}`,
            align: 'right',
        }),
        columnHelper.accessor('effectiveDate', {
            id: 'effectiveDate',
            header: 'Effective Date',
            cell: ({ value }) => formatDateTime(value, 'date-time'),
        }),
        columnHelper.accessor('expiryDate', {
            id: 'expiryDate',
            header: 'Expiry Date',
            cell: ({ value }) => formatDateTime(value, 'date-time'),
        }),
        columnHelper.accessor('hasTieredPricing', {
            id: 'hasTieredPricing',
            header: 'Pricing Type',
            cell: ({ value }) => (
                <Tag variant="outlined" color={value ? 'purple' : 'default'}>
                    {value ? 'Tiered' : 'Flat'}
                </Tag>
            ),
            width: 100,
        }),
        columnHelper.accessor('isActive', {
            id: 'isActive',
            header: 'Status',
            cell: ({ row }) => {
                const now = new Date();
                const expiry = row.expiryDate ? new Date(row.expiryDate) : null;
                const isExpired = expiry && expiry < now;
                const isExpiringSoon =
                    expiry && !isExpired && expiry.getTime() - now.getTime() <= EXPIRY_SOON_WINDOW_MS;

                if (!row.isActive) {
                    return <Badge status="default" text="Inactive" />;
                }
                if (isExpired) {
                    return <Badge status="error" text="Expired" />;
                }
                if (isExpiringSoon) {
                    return (
                        <Tooltip title={`Expires ${formatDateTime(row.expiryDate, 'date-time')}`}>
                            <Badge status="warning" text="Expiring soon" />
                        </Tooltip>
                    );
                }
                return <Badge status="success" text="Active" />;
            },
            width: 100,
        }),
        columnHelper.actions({
            id: 'actions',
            cell: ({ row }) => (
                <Space>
                    <Button.Edit
                        size="small"
                        tooltip={isReadOnly ? 'View pricing' : 'Edit pricing'}
                        onClick={() => setModelPricingAction(['edit', row.id])}
                    />
                    {!isReadOnly && (
                        <Button.Delete
                            size="small"
                            tooltip="Delete pricing"
                            confirmTitle="Delete Model Pricing"
                            confirmDescription="Are you sure you want to delete this pricing record?"
                            onConfirm={(onSuccess) =>
                                deleteModelPricing(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            message.success('Pricing deleted successfully');
                                            void refetch();
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    )}
                </Space>
            ),
            ...DEFAULT_ACTION_COLUMN,
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: modelPricing?.items || [],
        total: modelPricing?.items.length || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: {
            position: 'bottom',
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });
    const hasBlockingError = hasBlockingQueryError(error, modelPricing);

    const handleBackfillCosts = () => {
        modal.confirm({
            title: 'Backfill historical costs',
            content:
                "This will calculate and update costs for all historical token usage records that don't have costs. This operation may take a while. Continue?",
            okText: 'Start Backfill',
            cancelText: 'Cancel',
            onOk: () => {
                const hide = message.loading('Starting cost backfill...');
                backfillCosts(undefined, {
                    onSuccess: (result) => {
                        hide();
                        if (result.updated === 0) {
                            message.info(
                                `No records needed backfilling. All ${result.total} records already have costs calculated.`,
                            );
                        } else {
                            message.success(
                                `Successfully backfilled costs for ${result.updated} out of ${result.total} records`,
                            );
                        }
                        if (result.errors && result.errors.length > 0) {
                            errorMessage.show(result.errors);
                        }
                    },
                    onError: (error) => {
                        hide();
                        errorMessage.show(error);
                    },
                });
            },
        });
    };

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="ai" />
            <Space className="mb-4 flex flex-wrap justify-between">
                <Space wrap>
                    <Input.Search
                        placeholder="Search by model name"
                        allowClear
                        enterButton="Search"
                        className="w-64"
                        onSearch={(value) => {
                            setQueryParams((prev) => ({
                                ...prev,
                                modelName: value || undefined,
                            }));
                        }}
                    />

                    <Select
                        placeholder="Provider"
                        allowClear
                        className="w-32"
                        value={providerFilter}
                        onChange={setProviderFilter}
                        options={AI_PROVIDERS.map((p) => ({ label: p.label, value: p.value }))}
                    />

                    <SegmentedControl
                        variant="bordered"
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value as typeof statusFilter)}
                        options={[
                            { label: 'All', value: 'all' },
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' },
                        ]}
                        aria-label="Filter by status"
                    />
                </Space>

                {!isReadOnly && (
                    <Space wrap>
                        <Tooltip title="Import pricing from constants or OpenRouter API">
                            <Button type="secondary" icon={<Download />} onClick={() => setImportModalOpen(true)}>
                                Import Pricing
                            </Button>
                        </Tooltip>

                        <Tooltip title="Calculate costs for historical data">
                            <Button
                                type="secondary"
                                icon={<RefreshCw />}
                                onClick={handleBackfillCosts}
                                loading={isBackfilling}
                            >
                                Backfill Costs
                            </Button>
                        </Tooltip>

                        <Button type="primary" icon={<Plus />} onClick={() => setModelPricingAction(['create'])}>
                            Add Pricing
                        </Button>
                    </Space>
                )}
            </Space>

            {hasBlockingError ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    retrying={isFetching}
                    title="Couldn't load model pricing"
                />
            ) : (
                <>
                    {error && (
                        <InlineQueryError
                            error={error}
                            onRetry={() => void refetch()}
                            retrying={isFetching}
                            title="Couldn't refresh model pricing"
                            compact
                        />
                    )}
                    <PrismaTable {...tableProps} />
                </>
            )}

            <ModelPricingEdit
                action={modelPricingAction}
                onClose={() => {
                    setModelPricingAction(['none']);
                    void refetch();
                }}
                isReadOnly={isReadOnly}
            />

            <ImportModelPricingModal
                open={importModalOpen}
                onClose={() => {
                    setImportModalOpen(false);
                    void refetch();
                }}
            />
        </PageController>
    );
}
